/**
 * Countdown Timer for Early Access Campaign
 */

class CountdownTimer {
  constructor(element) {
    this.element = element;
    this.targetDate = this.getTargetDate();
    this.daysElement = element.querySelector('[data-days]');
    this.hoursElement = element.querySelector('[data-hours]');
    this.minutesElement = element.querySelector('[data-minutes]');
    this.secondsElement = element.querySelector('[data-seconds]');

    if (this.targetDate) {
      this.startTimer();
    }
  }

  getTargetDate() {
    const dateStr = this.element.dataset.countdownDate;
    const timeStr = this.element.dataset.countdownTime || '00:00';

    if (!dateStr) return null;

    // Convert DD-MM-YYYY to YYYY-MM-DD for JavaScript Date parsing
    const dateParts = dateStr.split('-');
    if (dateParts.length === 3) {
      // Assuming DD-MM-YYYY format
      const [day, month, year] = dateParts;
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      return new Date(`${isoDate}T${timeStr}:00`);
    }

    // Fallback for other formats
    return new Date(`${dateStr}T${timeStr}:00`);
  }

  startTimer() {
    this.updateTimer();
    this.interval = setInterval(() => {
      this.updateTimer();
    }, 1000);
  }

  updateTimer() {
    const now = new Date().getTime();
    const distance = this.targetDate.getTime() - now;

    if (distance < 0) {
      this.handleExpired();
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    this.updateDisplay(days, hours, minutes, seconds);
  }

  updateDisplay(days, hours, minutes, seconds) {
    if (this.daysElement) this.daysElement.textContent = this.padZero(days);
    if (this.hoursElement) this.hoursElement.textContent = this.padZero(hours);
    if (this.minutesElement) this.minutesElement.textContent = this.padZero(minutes);
    if (this.secondsElement) this.secondsElement.textContent = this.padZero(seconds);
  }

  padZero(num) {
    return num.toString().padStart(2, '0');
  }

  handleExpired() {
    if (this.interval) {
      clearInterval(this.interval);
    }

    this.updateDisplay(0, 0, 0, 0);
    this.element.classList.add('countdown-expired');

    // Optional: Hide expired countdown or show alternative message
    const expiredMessage = this.element.closest('.countdown-section')?.querySelector('.countdown-expired-message');
    if (expiredMessage) {
      expiredMessage.style.display = 'block';
    }
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}

/**
 * Form Integration for Early Access
 */
class EarlyAccessFormHandler {
  constructor() {
    this.forms = document.querySelectorAll('.early-access-form');
    this.init();
  }

  init() {
    this.forms.forEach(form => {
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const button = form.querySelector('.early-access-form__button');
    const errorElement = form.parentElement.querySelector('.early-access-form__message:not(.early-access-form__message--success)');
    const successElement = form.parentElement.querySelector('.early-access-form__message--success');

    // Show loading state
    this.setLoadingState(button, true);
    this.hideMessages([errorElement, successElement]);

    try {
      // Get form data
      const formData = new FormData(form);
      const email = formData.get('email');
      const firstName = formData.get('first_name');
      const birthdayDay = formData.get('birthday_day');
      const birthdayMonth = formData.get('birthday_month');
      const birthdayYear = formData.get('birthday_year');

      // Basic validation
      if (!this.validateEmail(email)) {
        throw new Error('Please enter a valid email address');
      }

      // Check if Klaviyo handler exists
      if (typeof window.processFormSubmission === 'function') {
        // Use existing Klaviyo integration
        window.processFormSubmission(form);
      } else {
        // Fallback: simulate submission
        await this.simulateSubmission();
        this.showSuccess(form, successElement);
      }

    } catch (error) {
      this.showError(errorElement, error.message);
    } finally {
      this.setLoadingState(button, false);
    }
  }

  validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  setLoadingState(button, isLoading) {
    if (isLoading) {
      button.classList.add('loading');
      button.disabled = true;
    } else {
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  hideMessages(elements) {
    elements.forEach(element => {
      if (element) {
        element.style.display = 'none';
      }
    });
  }

  showError(errorElement, message) {
    if (errorElement) {
      const errorText = errorElement.querySelector('.error-text');
      if (errorText) {
        errorText.textContent = message;
      }
      errorElement.style.display = 'flex';
    }
  }

  showSuccess(form, successElement) {
    if (successElement) {
      successElement.style.display = 'flex';
      form.style.display = 'none';
    }
  }

  async simulateSubmission() {
    // Simulate API call delay
    return new Promise(resolve => setTimeout(resolve, 1000));
  }
}

// Initialize countdown timers when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  const countdownElements = document.querySelectorAll('.countdown-timer');
  const timers = [];

  countdownElements.forEach(element => {
    const timer = new CountdownTimer(element);
    timers.push(timer);
  });

  // Initialize form handlers
  const formHandler = new EarlyAccessFormHandler();

  // Store references globally for potential external access
  window.countdownTimers = timers;
  window.earlyAccessFormHandler = formHandler;

  // Handle countdown expiration
  document.addEventListener('countdownExpired', function(event) {
    console.log('Countdown expired for element:', event.detail.element);
    // Add any additional logic for when countdown expires
  });

  // Clean up timers when page is unloaded
  window.addEventListener('beforeunload', function() {
    timers.forEach(timer => timer.destroy());
  });
});
